type ToastKind = 'info' | 'success' | 'error' | 'locked';

interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
}

// Minimal in-house store to avoid pulling in zustand as a dep for Phase 1.
function makeStore() {
  let items: ToastItem[] = [];
  const listeners = new Set<() => void>();
  return {
    get: () => items,
    subscribe(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); },
    push(kind: ToastKind, message: string) {
      const id = Math.random().toString(36).slice(2);
      items = [...items, { id, kind, message }];
      listeners.forEach((l) => l());
      setTimeout(() => this.dismiss(id), 4000);
    },
    dismiss(id: string) {
      items = items.filter((t) => t.id !== id);
      listeners.forEach((l) => l());
    },
  };
}
const store = makeStore();
export const toast = {
  info: (m: string) => store.push('info', m),
  success: (m: string) => store.push('success', m),
  error: (m: string) => store.push('error', m),
  // 0.1.7 — distinct kind for 423 LOCKED so the toast can render with
  // the warning hue instead of the danger hue. The server's LOCKED
  // message is already operator-readable; we just want the right color.
  locked: (m: string) => store.push('locked', m),
  // 0.1.7 — central dispatcher for ApiError instances. Routes 'LOCKED'
  // codes to the locked toast (yellow/warning hue) and everything else
  // to the regular error toast (red). Components should prefer this
  // over `toast.error(err.message)` so 423s render correctly.
  fromError(err: unknown, fallback = 'Request failed') {
    if (err && typeof err === 'object' && 'code' in err && (err as { code?: string }).code === 'LOCKED') {
      const msg = (err as { message?: string }).message ?? 'This organization is archived (read-only).';
      this.locked(msg);
      return;
    }
    if (err instanceof Error) this.error(err.message);
    else this.error(fallback);
  },
};

import { useEffect, useState } from 'react';
export function ToastRegion() {
  const [items, setItems] = useState(store.get());
  useEffect(() => {
    const unsub = store.subscribe(() => setItems([...store.get()]));
    return () => { unsub(); };
  }, []);
  return (
    <div className="toast-region" role="region" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind}`}>{t.message}</div>
      ))}
    </div>
  );
}
